<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import { frontendURL } from 'dashboard/helper/URLHelper';
import EquipeOnlineCard from './EquipeOnlineCard.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Aceleres: painel "Equipe Online". Presenca real (polling 10s) + toggle de
// participacao no rodizio de leads (so gestor), com redistribuicao ao desligar.
// Tambem mostra, em tempo real, os leads (API Oficial / inbox 82) ainda sem
// resposta de cada vendedor com o countdown de 4 min ate a reatribuicao, e
// quantos leads ele deixou vencer os 4 min hoje.
const POLL_MS = 10000;

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const loading = ref(true);
const hasError = ref(false);
const agents = ref([]);
const searchQuery = ref('');
const statusFilter = ref('');
const teamFilter = ref([]);
const defaultTeamIds = ref([]);
const availableTeams = ref([]);
const teamSelectionInitialized = ref(false);
const summary = ref({
  total: 0,
  active: 0,
  idle: 0,
  offline: 0,
  rodizio_off: 0,
  pending: 0,
  missed_today: 0,
  received_today: 0,
  answered_today: 0,
  waiting_today: 0,
  aurora_today: 0,
});
const overview = ref({ waiting: [], answered: [], aurora: [] });
const serverTime = ref(0);
const commercial = ref(true);
const lastUpdated = ref(null);
const toast = ref('');
let timer = null;
let toastTimer = null;
let realtimeDebounce = null;
let fastPollTimer = null;

// relogio ao vivo p/ o countdown: offset entre o relogio do servidor e o local,
// recalculado a cada poll. nowSec avanca de 1 em 1s mesmo entre polls.
const nowSec = ref(Math.floor(Date.now() / 1000));
let serverOffset = 0;
let tickTimer = null;

// quais linhas estao com a lista de leads pendentes / recebidos expandida
const expanded = ref({});
const expandedRecv = ref({});
const receivedModal = ref({ open: false, agent: null, tab: 'received_today' });
const receivedSearch = ref('');
const receivedSort = ref({ key: 'activity', direction: 'desc' });
const showReceivedModal = true;

// Vínculo do ID Salesforce do consultor (gestor).
const sfDraft = ref({});
const sfBusy = ref({});
const sfOpen = ref({});
const statusDropdownOpen = ref(false);
const teamDropdownOpen = ref(false);
const statusDropdownRef = ref(null);
const teamDropdownRef = ref(null);

// estado do modal de desativacao/redistribuicao
const modalOpen = ref(false);
const modalAgent = ref(null);
const redistribMode = ref('equal');
const targetId = ref(null);
const modalBusy = ref(false);

const STATUS_COLOR = { active: '#10b981', idle: '#f59e0b', offline: '#9ca3af' };
const statusColor = status => STATUS_COLOR[status] || STATUS_COLOR.offline;
const isRodizioEffective = agent =>
  agent.rodizio_effective_enabled ?? agent.rodizio_enabled;
const isRodizioParticipant = agent => isRodizioEffective(agent);

// Ordenação alfabética estável — garante que os cards não se movam entre polls
// a menos que o status/rodizio do agente mude de verdade.
const sortByName = arr =>
  [...arr].sort((a, b) =>
    (a.available_name || a.name || '').localeCompare(
      b.available_name || b.name || '',
      'pt-BR',
      { sensitivity: 'base' }
    )
  );

const presenceRequestOptions = () => {
  const options = {
    includeOutOfRotation: statusFilter.value === 'fora',
  };
  if (teamSelectionInitialized.value) options.teamIds = teamFilter.value;
  return options;
};

const fetchPresence = async () => {
  try {
    const { data } = await AceleresTeamPresenceAPI.getPresence(
      presenceRequestOptions()
    );
    availableTeams.value = data.available_teams || availableTeams.value;
    if (!teamSelectionInitialized.value) {
      defaultTeamIds.value = data.default_team_ids || [];
      teamFilter.value = data.selected_team_ids || defaultTeamIds.value;
      teamSelectionInitialized.value = true;
    }
    agents.value = data.payload || [];
    // hidrata o rascunho do ID Salesforce sem sobrescrever digitação em curso
    agents.value.forEach(a => {
      if (sfDraft.value[a.id] === undefined)
        sfDraft.value[a.id] = a.salesforce_id || '';
    });
    summary.value = data.summary || summary.value;
    overview.value = data.today_overview || {
      waiting: [],
      answered: [],
      aurora: [],
    };
    serverTime.value = data.server_time || 0;
    commercial.value = data.commercial !== false;
    serverOffset =
      (data.server_time || Math.floor(Date.now() / 1000)) -
      Math.floor(Date.now() / 1000);
    nowSec.value = Math.floor(Date.now() / 1000) + serverOffset;
    lastUpdated.value = new Date();
    hasError.value = false;
  } catch (error) {
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};

const refreshNow = () => {
  loading.value = true;
  fetchPresence();
};

const openHistory = () => {
  router.push(frontendURL(`accounts/${accountId}/equipe-online/historico`));
};

const onRealtimeRefresh = () => {
  if (realtimeDebounce) window.clearTimeout(realtimeDebounce);
  realtimeDebounce = window.setTimeout(() => {
    fetchPresence();
    realtimeDebounce = null;
  }, 800);
};

// Quando qualquer deadline cruza zero: busca imediata + fast-poll a cada 3s
// ate o lead sair de pending_leads (cron reassignou e broadcast chegou).
// Evita a janela de ate 60s onde o card ficava travado em 0:00.
// Teto de 30 iteracoes (~90s) para nao poluir em caso de cron ausente.
let fastPollCount = 0;
watch(nowSec, (newSec, oldSec) => {
  const justExpired = agents.value.some(a =>
    (a.pending_leads || []).some(
      l => l.deadline_at > oldSec && l.deadline_at <= newSec
    )
  );
  if (!justExpired || fastPollTimer) return;
  fastPollCount = 0;
  fetchPresence();
  fastPollTimer = window.setInterval(async () => {
    fastPollCount += 1;
    await fetchPresence();
    const stillPending = agents.value.some(a =>
      (a.pending_leads || []).some(l => l.deadline_at <= nowSec.value)
    );
    if (!stillPending || fastPollCount >= 30) {
      window.clearInterval(fastPollTimer);
      fastPollTimer = null;
      fastPollCount = 0;
    }
  }, 3000);
});

onMounted(() => {
  fetchPresence();
  timer = window.setInterval(fetchPresence, POLL_MS);
  tickTimer = window.setInterval(() => {
    nowSec.value = Math.floor(Date.now() / 1000) + serverOffset;
  }, 1000);
  document.addEventListener('click', onDocumentClick);
  emitter.on(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH, onRealtimeRefresh);
});

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
  if (tickTimer) window.clearInterval(tickTimer);
  if (toastTimer) window.clearTimeout(toastTimer);
  if (realtimeDebounce) window.clearTimeout(realtimeDebounce);
  if (fastPollTimer) window.clearInterval(fastPollTimer);
  document.removeEventListener('click', onDocumentClick);
  emitter.off(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH, onRealtimeRefresh);
});

const showToast = msg => {
  toast.value = msg;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.value = '';
  }, 4000);
};

const updatedLabel = () =>
  lastUpdated.value
    ? lastUpdated.value.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '';

const toggleExpand = id => {
  expanded.value = { ...expanded.value, [id]: !expanded.value[id] };
};
const toggleExpandRecv = id => {
  expandedRecv.value = { ...expandedRecv.value, [id]: !expandedRecv.value[id] };
};

const openReceivedModal = agent => {
  receivedModal.value = {
    open: true,
    agent,
    tab: 'received_today',
  };
  receivedSearch.value = '';
};

const closeReceivedModal = () => {
  receivedModal.value = { open: false, agent: null, tab: 'received_today' };
  receivedSearch.value = '';
};

const selectReceivedTab = tab => {
  receivedModal.value = { ...receivedModal.value, tab };
};

const toggleReceivedSort = key => {
  const direction =
    receivedSort.value.key === key && receivedSort.value.direction === 'desc'
      ? 'asc'
      : 'desc';
  receivedSort.value = { key, direction };
};

const receivedSortIcon = key => {
  if (receivedSort.value.key !== key) return 'i-lucide-arrow-up-down';
  return receivedSort.value.direction === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};

// ── Leads pendentes / countdown de 4 min ────────────────────────────────────

// "vencidos" = leads que o vendedor recebeu e não atendeu (lost)
const vencidosOf = agent => (agent.lost_leads || []).length;
const vencidosTotal = computed(() =>
  agents.value.reduce((sum, a) => sum + vencidosOf(a), 0)
);

// ── Modal de detalhe dos cards de resumo ────────────────────────────────────
const detailModal = ref({ open: false, type: null });
const detailSearch = ref('');
const DETAIL_CONFIG = {
  waiting: {
    title: 'Leads de hoje · sem resposta vs atendidos',
    kind: 'overview',
  },
  aurora: { title: 'Leads de hoje · atendidos pela Aurora', kind: 'overview' },
  received: { title: 'Recebidos hoje', kind: 'leads' },
  answered: { title: 'Atendidos hoje', kind: 'leads' },
  pending: { title: 'Aguardando resposta', kind: 'leads' },
  missed: { title: 'Vencidos hoje · recebeu e não atendeu', kind: 'leads' },
  active: { title: 'Vendedores ativos', kind: 'sellers' },
  idle: { title: 'Vendedores ociosos', kind: 'sellers' },
  offline: { title: 'Vendedores offline', kind: 'sellers' },
  rodizio_off: { title: 'Fora do rodízio', kind: 'sellers' },
  total: { title: 'Todos os vendedores', kind: 'sellers' },
};
const openDetail = type => {
  if (DETAIL_CONFIG[type]) {
    detailSearch.value = '';
    detailModal.value = { open: true, type };
  }
};
const closeDetail = () => {
  detailSearch.value = '';
  detailModal.value = { open: false, type: null };
};
const detailTitle = computed(() =>
  detailModal.value.type ? DETAIL_CONFIG[detailModal.value.type].title : ''
);
const detailKind = computed(() =>
  detailModal.value.type ? DETAIL_CONFIG[detailModal.value.type].kind : null
);

const leadExtractors = {
  received: a => (a.received_leads || []).map(l => ({ ...l, _kind: 'today' })),
  answered: a => [
    ...(a.received_leads || [])
      .filter(l => l.answered)
      .map(l => ({ ...l, _kind: 'today' })),
    ...(a.earlier_answered_leads || []).map(l => ({ ...l, _kind: 'earlier' })),
  ],
  pending: a => (a.pending_leads || []).map(l => ({ ...l, _kind: 'pending' })),
  missed: a => (a.lost_leads || []).map(l => ({ ...l, _kind: 'lost' })),
};

const detailLeadGroups = computed(() => {
  const extractor = leadExtractors[detailModal.value.type];
  if (!extractor) return [];
  return agents.value
    .map(a => ({ seller: a, leads: extractor(a) }))
    .filter(group => group.leads.length);
});

const detailSellers = computed(() => {
  const type = detailModal.value.type;
  if (type === 'total') return agents.value;
  if (type === 'rodizio_off')
    return agents.value.filter(a => !isRodizioParticipant(a));
  return agents.value.filter(a => a.status === type);
});

const detailFilteredSellers = computed(() => {
  const query = detailSearch.value.trim().toLowerCase();
  if (!query) return detailSellers.value;
  return detailSellers.value.filter(s =>
    `${s.available_name || ''} ${s.name || ''} ${s.email || ''}`
      .toLowerCase()
      .includes(query)
  );
});

// vendedores ativos (no rodizio) para o dropdown "vendedor especifico"
const activeSellersForModal = computed(() =>
  agents.value.filter(
    a =>
      isRodizioEffective(a) &&
      (!modalAgent.value || a.id !== modalAgent.value.id)
  )
);

const enableRodizio = async agent => {
  try {
    await AceleresTeamPresenceAPI.setRodizio({
      userId: agent.id,
      enabled: true,
    });
    showToast(`${agent.available_name || agent.name} voltou ao rodízio.`);
    await fetchPresence();
  } catch (error) {
    showToast('Não foi possível reativar. Tente novamente.');
  }
};

const onToggleRodizio = agent => {
  // Bloqueios de calendário/horário são efetivos e temporários. Não permita
  // que o gestor transforme esse estado automático em uma alteração manual.
  if (agent.rodizio_enabled && !isRodizioEffective(agent)) return;

  if (agent.rodizio_enabled) {
    // desligar -> abre modal de redistribuicao
    modalAgent.value = agent;
    redistribMode.value = 'equal';
    targetId.value = null;
    modalOpen.value = true;
  } else {
    enableRodizio(agent);
  }
};

const closeModal = () => {
  if (modalBusy.value) return;
  modalOpen.value = false;
  modalAgent.value = null;
};

const confirmDeactivate = async () => {
  if (!modalAgent.value) return;
  if (redistribMode.value === 'specific' && !targetId.value) return;
  modalBusy.value = true;
  try {
    const { data } = await AceleresTeamPresenceAPI.setRodizio({
      userId: modalAgent.value.id,
      enabled: false,
      redistribute: redistribMode.value,
      targetId: redistribMode.value === 'specific' ? targetId.value : null,
    });
    const name = modalAgent.value.available_name || modalAgent.value.name;
    const moved = data?.moved || 0;
    showToast(
      moved > 0
        ? `${name} saiu do rodízio — ${moved} conversa(s) redistribuída(s).`
        : `${name} saiu do rodízio.`
    );
    modalOpen.value = false;
    modalAgent.value = null;
    await fetchPresence();
  } catch (error) {
    showToast('Não foi possível desativar. Tente novamente.');
  } finally {
    modalBusy.value = false;
  }
};

const toggleSf = id => {
  sfOpen.value = { ...sfOpen.value, [id]: !sfOpen.value[id] };
};

const saveSalesforceId = async agent => {
  const value = (sfDraft.value[agent.id] || '').trim();
  if (value && !/^[a-zA-Z0-9]{15,18}$/.test(value)) {
    showToast('ID Salesforce inválido — use 15 a 18 caracteres alfanuméricos.');
    return;
  }
  sfBusy.value[agent.id] = true;
  try {
    await AceleresTeamPresenceAPI.setSalesforceId({
      userId: agent.id,
      salesforceId: value,
    });
    agent.salesforce_id = value || null;
    const name = agent.available_name || agent.name;
    showToast(
      value
        ? `ID Salesforce vinculado a ${name}.`
        : `ID Salesforce removido de ${name}.`
    );
    sfOpen.value = { ...sfOpen.value, [agent.id]: false };
  } catch (error) {
    showToast('Não foi possível salvar o ID Salesforce.');
  } finally {
    sfBusy.value[agent.id] = false;
  }
};

// ── Helpers para o modal de detalhe ─────────────────────────────────────────
const fmtHora = epoch =>
  epoch
    ? new Date(epoch * 1000).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--:--';

const fmtDuration = mins => {
  if (mins === null || mins === undefined) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
};

const slaColor = mins => {
  if (mins === null || mins === undefined) return '#94a3b8';
  if (mins <= 5) return '#10b981';
  if (mins <= 30) return '#f59e0b';
  return '#ef4444';
};

const urgencyColor = secs => {
  if (secs <= 0) return '#dc2626';
  if (secs <= 60) return '#ef4444';
  if (secs <= 120) return '#f59e0b';
  return '#10b981';
};

const remainingFor = lead => lead.deadline_at - nowSec.value;

const fmtCountdown = secs => {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const convUrl = displayId =>
  `/app/accounts/${accountId}/conversations/${displayId}`;

const leadInitials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0] || '')
    .join('')
    .toUpperCase();

const leadAvatarClasses = [
  'bg-blue-100 text-blue-700',
  'bg-teal-100 text-teal-700',
  'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700',
  'bg-red-100 text-red-700',
  'bg-green-100 text-green-700',
  'bg-pink-100 text-pink-700',
];

const leadAvatarClass = lead => {
  const index =
    Math.abs(Number(lead?.display_id) || 0) % leadAvatarClasses.length;
  return leadAvatarClasses[index];
};

const receivedTabConfigs = computed(() => {
  const agent = receivedModal.value.agent;
  const receivedTodayCount = (agent?.received_leads || []).length;
  const receivedBeforeCount = (agent?.earlier_answered_leads || []).length;
  const notAnsweredCount = (agent?.lost_leads || []).length;

  return [
    {
      key: 'received_today',
      label: 'Recebidos hoje',
      count: receivedTodayCount,
      description: 'Leads recebidos e atendidos hoje.',
    },
    {
      key: 'received_before',
      label: 'Recebidos antes, atendidos hoje',
      count: receivedBeforeCount,
      description: 'Leads que chegaram antes e foram atendidos hoje.',
    },
    {
      key: 'lost',
      label: 'Recebeu mas não atendeu',
      count: notAnsweredCount,
      description: 'Leads recebidos sem atendimento dentro do prazo.',
    },
  ];
});

const receivedActiveTab = computed(
  () =>
    receivedTabConfigs.value.find(tab => tab.key === receivedModal.value.tab) ||
    receivedTabConfigs.value[0]
);

const receivedResponseLabel = lead => {
  if (
    lead.answered &&
    lead.response_minutes !== null &&
    lead.response_minutes !== undefined
  ) {
    return fmtDuration(lead.response_minutes);
  }
  if (lead.answered) return 'Atendida';
  return 'Sem resposta';
};

const receivedResponseClass = lead => {
  if (!lead.answered) return 'bg-red-50 text-red-600 border-red-100';
  if (lead.response_minutes === null || lead.response_minutes === undefined) {
    return 'bg-n-alpha-2 text-n-slate-11 border-n-weak';
  }
  if (lead.response_minutes <= 10)
    return 'bg-green-50 text-green-600 border-green-100';
  if (lead.response_minutes <= 60)
    return 'bg-orange-50 text-orange-500 border-orange-100';
  return 'bg-red-50 text-red-600 border-red-100';
};

const receivedRows = computed(() => {
  const agent = receivedModal.value.agent;
  if (!agent) return [];

  const toRow = (lead, kind) => {
    const entryAt =
      lead.first_inbound_at || lead.assigned_at || lead.created_at || null;
    const replyAt = lead.first_reply_at || null;
    const answered = !!lead.answered || !!replyAt;
    return {
      kind,
      display_id: lead.display_id,
      contact_name: lead.contact_name,
      entry_at: entryAt,
      reply_at: replyAt,
      answered,
      response_minutes: lead.response_minutes,
      response_type: receivedResponseLabel({ ...lead, answered }),
      response_label: answered ? fmtHora(replyAt) : '—',
      search_text:
        `${lead.contact_name || ''} ${lead.display_id || ''}`.toLowerCase(),
    };
  };

  let rows = [];
  switch (receivedModal.value.tab) {
    case 'received_before':
      rows = (agent.earlier_answered_leads || []).map(lead =>
        toRow(lead, 'received_before')
      );
      break;
    case 'lost':
      rows = (agent.lost_leads || []).map(lead => toRow(lead, 'lost'));
      break;
    case 'received_today':
    default:
      rows = (agent.received_leads || []).map(lead =>
        toRow(lead, 'received_today')
      );
      break;
  }

  const query = receivedSearch.value.trim().toLowerCase();
  if (query) {
    rows = rows.filter(row => row.search_text.includes(query));
  }

  return rows.sort((a, b) => {
    const sortKey = receivedSort.value.key;
    const direction = receivedSort.value.direction === 'asc' ? 1 : -1;
    const atA =
      sortKey === 'activity' ? a.reply_at || a.entry_at || 0 : a[sortKey] || 0;
    const atB =
      sortKey === 'activity' ? b.reply_at || b.entry_at || 0 : b[sortKey] || 0;
    return (atA - atB) * direction;
  });
});

const summaryCards = computed(() => [
  {
    key: 'received',
    label: 'Recebidos hoje',
    value: summary.value.received_today,
    color: '#6366f1',
    icon: 'i-lucide-inbox',
  },
  {
    key: 'answered',
    label: 'Atendidos hoje',
    value: summary.value.answered_today,
    color: '#10b981',
    icon: 'i-lucide-message-circle-more',
  },
  {
    key: 'waiting',
    label: 'Sem resposta hoje',
    value: summary.value.waiting_today,
    color: '#dc2626',
    icon: 'i-lucide-clock-3',
  },
  {
    key: 'aurora',
    label: 'Atendido Aurora',
    value: summary.value.aurora_today,
    color: '#a855f7',
    icon: 'i-lucide-sparkles',
  },
  {
    key: 'pending',
    label: 'Aguardando resposta',
    value: summary.value.pending,
    color: '#f59e0b',
    icon: 'i-lucide-hourglass',
  },
  {
    key: 'missed',
    label: 'Vencidos hoje',
    value: vencidosTotal.value,
    color: '#dc2626',
    icon: 'i-lucide-alert-triangle',
  },
  {
    key: 'active',
    label: 'Online',
    value: summary.value.active,
    color: '#10b981',
    icon: 'i-lucide-circle-check-big',
  },
  {
    key: 'idle',
    label: 'Ociosos',
    value: summary.value.idle,
    color: '#f59e0b',
    icon: 'i-lucide-coffee',
  },
  {
    key: 'offline',
    label: 'Offline',
    value: summary.value.offline,
    color: '#9ca3af',
    icon: 'i-lucide-wifi-off',
  },
  {
    key: 'rodizio_off',
    label: 'Desativado',
    value: summary.value.rodizio_off,
    color: '#ef4444',
    icon: 'i-lucide-pause-circle',
  },
  {
    key: 'total',
    label: 'Total',
    value: summary.value.total,
    color: '#6366f1',
    icon: 'i-lucide-layers-3',
  },
]);

const statusOptions = [
  { value: '', label: 'Todos os status' },
  { value: 'active', label: 'Online' },
  { value: 'idle', label: 'Ocioso' },
  { value: 'offline', label: 'Offline' },
  { value: 'fora', label: 'Desativado' },
];

const teamOptions = computed(() => {
  return [...availableTeams.value].sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  );
});

const selectedStatusLabel = computed(
  () =>
    statusOptions.find(option => option.value === statusFilter.value)?.label ||
    'Todos os status'
);
const selectedTeamIdsAreDefault = computed(() => {
  const selected = [...teamFilter.value].map(String).sort();
  const defaults = [...defaultTeamIds.value].map(String).sort();
  return (
    selected.length === defaults.length &&
    selected.every((id, index) => id === defaults[index])
  );
});
const selectedTeamLabel = computed(() => {
  if (!teamFilter.value.length) return 'Nenhum time selecionado';
  if (selectedTeamIdsAreDefault.value) return 'Times padrão do usuário';
  if (teamFilter.value.length === 1) {
    return (
      teamOptions.value.find(
        team => String(team.id) === String(teamFilter.value[0])
      )?.name || '1 time selecionado'
    );
  }
  return `${teamFilter.value.length} times selecionados`;
});

const closeFilterDropdowns = () => {
  statusDropdownOpen.value = false;
  teamDropdownOpen.value = false;
};

const onDocumentClick = event => {
  const target = event.target;
  if (
    statusDropdownRef.value?.contains(target) ||
    teamDropdownRef.value?.contains(target)
  ) {
    return;
  }
  closeFilterDropdowns();
};

const selectStatusFilter = value => {
  statusFilter.value = value;
  statusDropdownOpen.value = false;
  loading.value = true;
  fetchPresence();
};

const toggleTeamFilter = teamId => {
  const normalizedId = String(teamId);
  const selected = teamFilter.value.map(String);
  const next = selected.includes(normalizedId)
    ? selected.filter(id => id !== normalizedId)
    : [...selected, normalizedId];

  teamFilter.value = next;
  loading.value = true;
  fetchPresence();
};

const isTeamSelected = teamId =>
  teamFilter.value.map(String).includes(String(teamId));

const visibleAgents = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const status = statusFilter.value;
  const teamIds = teamFilter.value.map(String);

  return sortByName(agents.value).filter(agent => {
    const matchesStatus =
      !status ||
      (status === 'fora'
        ? !isRodizioEffective(agent)
        : agent.status === status);
    const matchesTeam =
      teamIds.length > 0 &&
      (agent.teams || []).some(team => teamIds.includes(String(team.id)));
    const haystack = [
      agent.available_name,
      agent.name,
      agent.email,
      ...(agent.teams || []).map(team => team.name),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesSearch = !query || haystack.includes(query);

    return matchesStatus && matchesTeam && matchesSearch;
  });
});

const clearFilters = () => {
  searchQuery.value = '';
  statusFilter.value = '';
  teamFilter.value = [...defaultTeamIds.value];
  closeFilterDropdowns();
  loading.value = true;
  fetchPresence();
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-[#f6f6f6]">
    <header
      class="sticky top-0 z-[100] flex flex-shrink-0 flex-col items-stretch justify-between gap-3 bg-[#f6f6f6] px-4 py-3 isolate transition-[padding] duration-200 ease-out before:pointer-events-none before:absolute before:bottom-0 before:left-4 before:right-4 before:h-px before:bg-[rgba(226,232,240,0.9)] md:flex-row md:items-center md:gap-4 md:px-10 md:py-4 md:before:left-10 md:before:right-10"
    >
      <div class="min-w-0">
        <h1 class="text-xl font-bold truncate text-n-slate-12 md:text-[28px]">
          Equipe Online
        </h1>
        <p class="mt-1 text-sm text-n-slate-11 md:text-base">
          Vendedores conectados, ativos e no rodízio em tempo real
        </p>
      </div>
      <div
        class="acl-mobile-account-menu-host flex flex-wrap items-center flex-shrink-0 gap-2 md:flex-nowrap md:gap-3"
      >
        <div
          v-if="!commercial || lastUpdated"
          class="flex w-full flex-col items-start gap-1 md:w-auto md:items-end"
        >
          <span v-if="!commercial" class="text-xs font-medium text-n-amber-11">
            Fora do horário comercial — rodízio pausado (Aurora atende)
          </span>
          <span v-if="lastUpdated" class="text-sm font-medium text-n-slate-12">
            Atualizado às {{ updatedLabel() }}
          </span>
        </div>
        <button
          type="button"
          class="inline-flex flex-1 items-center justify-center gap-2 h-10 px-3 text-sm font-semibold rounded-lg border border-0 bg-[#4f46e5] text-white hover:bg-[#4338ca] shadow-sm transition-colors md:flex-none md:px-4"
          @click="openHistory"
        >
          <span class="i-lucide-clock-3 size-4" />
          Histórico
        </button>
        <button
          type="button"
          class="inline-flex flex-1 items-center justify-center gap-2 h-10 px-3 text-sm font-semibold rounded-lg transition-colors md:flex-none md:px-4"
          style="
            background-color: #059669 !important;
            color: #ffffff !important;
            border: 0 !important;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08) !important;
          "
          @click="refreshNow"
        >
          <span class="i-lucide-refresh-cw size-4" />
          Atualizar
        </button>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
      <div
        class="grid flex-shrink-0 gap-4 px-4 py-4 grid-cols-1 md:grid-cols-2 md:px-6 xl:grid-cols-5"
      >
        <button
          v-for="card in summaryCards"
          :key="card.key"
          type="button"
          class="flex items-center gap-4 w-full px-5 py-5 text-left transition-all border rounded-2xl border-n-weak bg-n-surface-1 min-w-0 min-h-[92px] shadow-md shadow-black/5 hover:border-n-slate-8 hover:bg-n-alpha-1 hover:shadow-lg hover:-translate-y-0.5"
          title="Clique para ver os detalhes"
          @click="openDetail(card.key)"
        >
          <span
            class="inline-flex items-center justify-center flex-shrink-0 rounded-full size-11 shadow-sm"
            :style="{ backgroundColor: `${card.color}14`, color: card.color }"
          >
            <span :class="card.icon" class="size-5" />
          </span>
          <div class="flex flex-col min-w-0">
            <span class="text-2xl font-semibold leading-none text-n-slate-12">{{
              card.value
            }}</span>
            <span class="text-base text-n-slate-11 truncate">{{
              card.label
            }}</span>
          </div>
        </button>
      </div>

      <div class="px-4 py-5 mt-4 md:px-6 md:mt-12">
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 px-4 py-4 shadow-sm"
        >
          <div
            class="flex flex-col items-stretch justify-between gap-3 pb-3 mb-4 border-b border-n-weak md:flex-row md:items-center"
          >
            <div>
              <h2
                class="text-base font-semibold tracking-wide uppercase text-n-slate-12"
              >
                Filtros
              </h2>
            </div>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 h-10 rounded-xl border border-n-weak px-4 text-sm font-semibold text-n-slate-12 transition-colors hover:border-n-slate-8 hover:bg-transparent disabled:opacity-40 disabled:hover:border-n-weak disabled:hover:bg-transparent md:self-auto"
              :disabled="!searchQuery && !statusFilter && selectedTeamIdsAreDefault"
              @click="clearFilters"
            >
              <span class="i-lucide-eraser size-4" />
              Limpar filtros
            </button>
          </div>

          <div
            class="relative z-30 flex flex-col items-stretch gap-3 overflow-visible md:flex-row md:flex-nowrap md:items-end"
          >
            <div class="w-full min-w-0 flex-1 md:min-w-[240px]">
              <label
                class="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-n-slate-11"
              >
                Buscar
              </label>
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Buscar vendedor"
                class="reset-base no-margin block w-full !h-11 rounded-lg leading-[2.75rem] border border-solid border-[#d4d7dd] bg-transparent appearance-none px-3.5 text-sm text-n-slate-12 shadow-sm outline-none ring-0 placeholder:text-n-slate-10 focus:border-[#d4d7dd] focus:ring-0 !mb-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none"
              />
            </div>
            <div
              ref="statusDropdownRef"
              class="relative w-full min-w-0 md:min-w-[180px] md:w-auto"
            >
              <label
                class="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-n-slate-11"
              >
                Status
              </label>
              <button
                type="button"
                class="flex items-center justify-between w-full h-11 gap-2 px-3.5 text-sm leading-[2.75rem] text-left border border-solid border-[#d4d7dd] rounded-lg bg-transparent text-n-slate-12 shadow-sm outline-none ring-0 transition-colors hover:border-n-brand focus:border-[#d4d7dd] focus:ring-0"
                @click.stop="statusDropdownOpen = !statusDropdownOpen"
              >
                <span class="truncate">{{ selectedStatusLabel }}</span>
                <span
                  class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10"
                />
              </button>
              <div
                v-if="statusDropdownOpen"
                class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-[200] overflow-hidden rounded-xl border border-n-slate-7 bg-white shadow-xl dark:bg-n-solid-2 scrollbar-thin"
              >
                <button
                  v-for="option in statusOptions"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center justify-between px-3 py-2.5 text-left text-base text-n-slate-12 transition-colors hover:bg-n-alpha-2"
                  @click.stop="selectStatusFilter(option.value)"
                >
                  <span>{{ option.label }}</span>
                  <span
                    v-if="option.value === statusFilter"
                    class="i-lucide-check size-4 text-emerald-600"
                  />
                </button>
              </div>
            </div>
            <div
              ref="teamDropdownRef"
              class="relative w-full min-w-0 md:min-w-[200px] md:w-auto"
            >
              <label
                class="mb-1.5 block text-sm font-semibold uppercase tracking-wide text-n-slate-11"
              >
                Times
              </label>
              <button
                type="button"
                class="flex items-center justify-between w-full h-11 gap-2 px-3.5 text-sm leading-[2.75rem] text-left border border-solid border-[#d4d7dd] rounded-lg bg-transparent text-n-slate-12 shadow-sm outline-none ring-0 transition-colors hover:border-n-brand focus:border-[#d4d7dd] focus:ring-0"
                @click.stop="teamDropdownOpen = !teamDropdownOpen"
              >
                <span class="truncate">{{ selectedTeamLabel }}</span>
                <span
                  class="i-lucide-chevron-down size-4 flex-shrink-0 text-n-slate-10"
                />
              </button>
              <div
                v-if="teamDropdownOpen"
                class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-[200] max-h-64 overflow-y-auto rounded-xl border border-n-slate-7 bg-white shadow-xl dark:bg-n-solid-2 scrollbar-thin"
              >
                <label
                  v-for="team in teamOptions"
                  :key="team.id"
                  class="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left text-base text-n-slate-12 transition-colors hover:bg-n-alpha-2"
                >
                  <input
                    type="checkbox"
                    class="size-4 rounded border-n-slate-7 text-emerald-600 focus:ring-emerald-500"
                    :checked="isTeamSelected(team.id)"
                    @change="toggleTeamFilter(team.id)"
                  />
                  <span class="truncate">{{ team.name }}</span>
                </label>
                <p
                  v-if="!teamOptions.length"
                  class="px-3 py-2.5 text-sm text-n-slate-10"
                >
                  Nenhum time disponível.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4 pt-5 pb-6 md:px-6">
        <div
          v-if="loading && !agents.length"
          class="py-16 text-sm text-center text-n-slate-10"
        >
          Carregando equipe…
        </div>
        <div
          v-else-if="hasError"
          class="py-16 text-sm text-center text-n-ruby-11"
        >
          Não foi possível carregar a presença da equipe. Tentando novamente…
        </div>
        <div
          v-else-if="!agents.length"
          class="py-16 text-sm text-center text-n-slate-10"
        >
          Nenhum vendedor cadastrado nesta conta.
        </div>
        <div
          v-else-if="visibleAgents.length"
          class="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
        >
          <EquipeOnlineCard
            v-for="agent in visibleAgents"
            :key="agent.id"
            :agent="agent"
            :account-id="accountId"
            :commercial="commercial"
            :now-sec="nowSec"
            :server-time="serverTime"
            :is-expanded="!!expanded[agent.id]"
            :is-expanded-recv="!!expandedRecv[agent.id]"
            :sf-is-open="!!sfOpen[agent.id]"
            :sf-draft-value="sfDraft[agent.id] ?? ''"
            :sf-is-busy="!!sfBusy[agent.id]"
            @toggle-expand="toggleExpand(agent.id)"
            @open-received-modal="openReceivedModal(agent)"
            @toggle-rodizio="onToggleRodizio(agent)"
            @toggle-sf="toggleSf(agent.id)"
            @update-sf-draft="
              v => {
                sfDraft[agent.id] = v;
              }
            "
            @save-salesforce-id="saveSalesforceId(agent)"
          />
        </div>
        <div v-else class="py-16 text-sm text-center text-n-slate-10">
          Nenhum vendedor encontrado com os filtros atuais.
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showReceivedModal && receivedModal.open"
        class="fixed inset-0 z-[10050] flex items-center justify-center p-2 sm:p-4"
        style="background: rgba(0, 0, 0, 0.45)"
        @click.self="closeReceivedModal"
      >
        <div
          class="flex w-full max-w-6xl max-h-[86vh] flex-col overflow-hidden rounded-xl border border-n-weak bg-n-surface-1 shadow-2xl"
        >
          <div
            class="flex items-start justify-between gap-3 px-4 pb-4 pt-4 sm:gap-4 sm:px-6 sm:pt-5"
          >
            <div class="flex min-w-0 items-start gap-3">
              <span
                class="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-lg bg-n-slate-3 text-black"
              >
                <span class="i-lucide-inbox size-5" />
              </span>
              <div class="min-w-0">
                <h2 class="m-0 text-xl font-semibold text-n-slate-12">
                  Recebidos
                </h2>
                <p class="mb-0 mt-2 text-sm leading-6 text-n-slate-11">
                  Acompanhe os leads recebidos e o status de atendimento.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-lg border border-transparent text-n-slate-10 transition-colors hover:rounded-full hover:bg-n-alpha-2 hover:text-n-slate-12"
              aria-label="Fechar modal de recebidos"
              @click="closeReceivedModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <div
            class="flex flex-col gap-4 overflow-hidden px-3 pb-3 sm:px-6 sm:pb-4"
          >
            <div class="flex flex-wrap items-center gap-3">
              <div
                class="grid w-full min-w-0 flex-1 grid-cols-3 overflow-hidden rounded-lg border border-n-weak bg-n-surface-2 sm:min-w-[520px]"
              >
                <button
                  v-for="tab in receivedTabConfigs"
                  :key="tab.key"
                  type="button"
                  class="inline-flex h-10 min-w-0 items-center justify-center gap-1 border-r border-n-weak px-1 text-[11px] font-semibold text-n-slate-11 transition-colors last:border-r-0 hover:bg-n-alpha-1 hover:text-n-slate-12 sm:gap-2 sm:px-3 sm:text-xs"
                  :class="
                    receivedModal.tab === tab.key
                      ? 'bg-n-slate-3 text-green-700'
                      : ''
                  "
                  @click="selectReceivedTab(tab.key)"
                >
                  <span class="truncate">{{ tab.label }}</span>
                  <span
                    class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold shadow-sm"
                    :class="
                      receivedModal.tab === tab.key
                        ? 'bg-green-100 text-green-700'
                        : 'bg-white text-n-slate-11'
                    "
                  >
                    {{ tab.count }}
                  </span>
                </button>
              </div>

              <div
                class="relative w-full min-w-0 flex-1 sm:min-w-[280px] md:max-w-[340px]"
              >
                <input
                  v-model="receivedSearch"
                  type="search"
                  placeholder="Buscar por nome ou ID do lead..."
                  class="reset-base no-margin block h-10 w-full appearance-none rounded-lg border border-solid border-n-weak bg-n-surface-1 pl-9 pr-3 text-sm text-n-slate-12 outline-none ring-0 placeholder:text-n-slate-10 focus:border-violet-300 focus:ring-0 !mb-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none"
                />
                <span
                  class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
                />
              </div>
            </div>

            <div
              class="overflow-hidden rounded-xl border border-n-weak bg-white p-3 shadow-sm dark:bg-n-surface-1 sm:p-4"
            >
              <div class="mb-5 flex items-start gap-3">
                <span
                  class="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-600"
                >
                  <span class="i-lucide-circle-check-big size-6" />
                </span>
                <div class="min-w-0">
                  <h3 class="m-0 text-base font-semibold text-n-slate-12">
                    {{ receivedActiveTab.label }} ({{
                      receivedActiveTab.count
                    }})
                  </h3>
                  <p class="mb-0 mt-1 text-sm text-n-slate-11">
                    {{ receivedActiveTab.description }}
                  </p>
                </div>
              </div>

              <div class="max-h-[56vh] overflow-auto">
                <table class="min-w-full border-separate border-spacing-0">
                  <thead class="sticky top-0 z-10">
                    <tr
                      class="bg-[#edf1f5] text-left text-sm font-bold text-n-slate-12"
                    >
                      <th class="rounded-l-lg bg-[#edf1f5] px-4 py-4">Lead</th>
                      <th class="bg-[#edf1f5] px-4 py-4">ID do lead</th>
                      <th class="bg-[#edf1f5] px-4 py-4">
                        <button
                          type="button"
                          class="inline-flex items-center gap-2 font-bold text-n-slate-12 transition-colors hover:text-green-700"
                          @click="toggleReceivedSort('entry_at')"
                        >
                          Entrada
                          <span
                            :class="receivedSortIcon('entry_at')"
                            class="size-4 text-green-700"
                          />
                        </button>
                      </th>
                      <th class="bg-[#edf1f5] px-4 py-4">
                        <button
                          type="button"
                          class="inline-flex items-center gap-2 font-bold text-n-slate-12 transition-colors hover:text-green-700"
                          @click="toggleReceivedSort('reply_at')"
                        >
                          Respondida
                          <span
                            :class="receivedSortIcon('reply_at')"
                            class="size-4 text-green-700"
                          />
                        </button>
                      </th>
                      <th class="bg-[#edf1f5] px-4 py-4">Tipo de resposta</th>
                      <th class="rounded-r-lg bg-[#edf1f5] px-4 py-4">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!receivedRows.length">
                      <td
                        colspan="6"
                        class="px-4 py-10 text-center text-sm text-n-slate-10"
                      >
                        Nenhum lead nesta aba.
                      </td>
                    </tr>
                    <tr
                      v-for="lead in receivedRows"
                      :key="`${receivedModal.tab}-${lead.display_id}`"
                      class="group hover:bg-n-alpha-1"
                    >
                      <td class="border-b border-n-weak px-4 py-4">
                        <div class="flex items-center gap-4">
                          <div
                            class="flex size-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold"
                            :class="leadAvatarClass(lead)"
                          >
                            {{ leadInitials(lead.contact_name) }}
                          </div>
                          <div
                            class="min-w-0 truncate text-base font-semibold text-n-slate-12"
                          >
                            {{ lead.contact_name }}
                          </div>
                        </div>
                      </td>
                      <td
                        class="border-b border-n-weak px-4 py-4 text-sm font-medium text-n-slate-11"
                      >
                        #{{ lead.display_id }}
                      </td>
                      <td
                        class="border-b border-n-weak px-4 py-4 text-sm font-medium text-n-slate-11"
                      >
                        <span class="inline-flex items-center gap-3">
                          <span
                            class="i-lucide-clock-3 size-5 text-n-slate-9"
                          />
                          {{ fmtHora(lead.entry_at) }}
                        </span>
                      </td>
                      <td
                        class="border-b border-n-weak px-4 py-4 text-sm font-medium text-n-slate-11"
                      >
                        <span class="inline-flex items-center gap-3">
                          <span
                            class="i-lucide-clock-3 size-5 text-n-slate-9"
                          />
                          {{ lead.response_label }}
                        </span>
                      </td>
                      <td class="border-b border-n-weak px-4 py-4">
                        <span
                          class="inline-flex min-w-[86px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold"
                          :class="receivedResponseClass(lead)"
                        >
                          <span class="i-lucide-timer size-4" />
                          {{ lead.response_type }}
                        </span>
                      </td>
                      <td class="border-b border-n-weak px-4 py-4">
                        <a
                          :href="convUrl(lead.display_id)"
                          class="inline-flex h-11 min-w-[174px] items-center justify-center gap-2 rounded-lg border border-violet-100 bg-white px-4 text-sm font-semibold text-violet-700 shadow-sm transition-colors hover:bg-violet-50"
                        >
                          <span
                            class="i-lucide-message-square size-4 text-violet-700"
                          />
                          Abrir conversa
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Modal de detalhe de um card de resumo -->
    <teleport to="body">
      <div
        v-if="detailModal.open"
        class="fixed inset-0 z-[10050] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4"
        @click.self="closeDetail"
      >
        <div
          class="relative z-[10060] flex max-h-[calc(100vh-2rem)] w-full max-w-lg min-w-0 flex-col overflow-hidden rounded-[12px] border border-n-weak bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
          role="dialog"
          aria-modal="true"
          :aria-label="detailTitle"
        >
          <div
            class="flex items-start justify-between flex-shrink-0 gap-4 border-b border-n-weak px-5 py-4"
          >
            <div class="min-w-0">
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ detailTitle }}
              </h2>
              <p
                v-if="detailModal.type === 'offline'"
                class="mb-0 mt-1 text-sm leading-5 text-n-slate-11"
              >
                Estes vendedores estão atualmente offline.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex size-9 items-center justify-center rounded-full border border-n-weak bg-white text-n-slate-11 transition-colors hover:bg-n-slate-2 hover:text-n-slate-12"
              @click="closeDetail"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <div
            class="scrollbar-thin flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain px-5 pb-5 pt-4"
          >
            <!-- Visão por lead: aguardando atendimento vs já atendidos -->
            <template v-if="detailKind === 'overview'">
              <div class="flex flex-col gap-1">
                <span
                  class="px-2 text-[11px] font-semibold tracking-wide uppercase text-n-ruby-11"
                >
                  🔴 Aguardando atendimento ({{ overview.waiting.length }})
                </span>
                <div
                  v-if="!overview.waiting.length"
                  class="px-2 py-2 text-xs text-n-slate-10"
                >
                  Nenhum lead sem resposta no momento. 🎉
                </div>
                <a
                  v-for="lead in overview.waiting"
                  :key="'w-' + lead.display_id"
                  :href="convUrl(lead.display_id)"
                  class="flex flex-col gap-0.5 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
                >
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate text-n-slate-12">
                      {{ lead.contact_name }}
                      <span class="text-n-slate-9">#{{ lead.display_id }}</span>
                    </span>
                    <span class="flex-shrink-0 text-[11px] text-n-slate-10">
                      espera desde {{ fmtHora(lead.first_inbound_at) }}
                    </span>
                  </span>
                  <span class="text-[11px] text-n-slate-10">
                    agora com
                    <span class="font-semibold text-n-slate-12">{{
                      lead.current_assignee || '—'
                    }}</span>
                    <span v-if="lead.passed_by && lead.passed_by.length">
                      · passou sem atender por {{ lead.passed_by.join(', ') }}
                    </span>
                    <span
                      v-if="lead.exhausted"
                      class="font-semibold text-n-ruby-11"
                      >· esgotou o rodízio</span
                    >
                  </span>
                </a>
              </div>

              <div class="flex flex-col gap-1">
                <span
                  class="px-2 text-[11px] font-semibold tracking-wide uppercase text-emerald-600"
                >
                  🟢 Já atendidos ({{ overview.answered.length }})
                </span>
                <a
                  v-for="lead in overview.answered"
                  :key="'a-' + lead.display_id"
                  :href="convUrl(lead.display_id)"
                  class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
                >
                  <span class="truncate text-n-slate-12">
                    {{ lead.contact_name }}
                    <span class="text-n-slate-9">#{{ lead.display_id }}</span>
                  </span>
                  <span
                    class="flex items-center flex-shrink-0 gap-2 text-[11px]"
                  >
                    <span class="text-n-slate-10"
                      >por {{ lead.answered_by }}</span
                    >
                    <span
                      class="font-semibold"
                      :style="{ color: slaColor(lead.response_minutes) }"
                    >
                      ⏱ {{ fmtDuration(lead.response_minutes) }}
                    </span>
                  </span>
                </a>
              </div>
              <div
                v-if="overview.aurora && overview.aurora.length"
                class="flex flex-col gap-1"
              >
                <span
                  class="px-2 text-[11px] font-semibold tracking-wide uppercase"
                  style="color: #a855f7"
                >
                  🟣 Atendido pela Aurora ({{ overview.aurora.length }})
                </span>
                <a
                  v-for="lead in overview.aurora"
                  :key="'au-' + lead.display_id"
                  :href="convUrl(lead.display_id)"
                  class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
                >
                  <span class="truncate text-n-slate-12">
                    {{ lead.contact_name }}
                    <span class="text-n-slate-9">#{{ lead.display_id }}</span>
                  </span>
                  <span
                    class="flex items-center flex-shrink-0 gap-2 text-[11px]"
                  >
                    <span class="text-n-slate-10"
                      >Aurora · agora com
                      {{ lead.current_assignee || '—' }}</span
                    >
                    <span
                      class="font-semibold"
                      :style="{ color: slaColor(lead.response_minutes) }"
                    >
                      ⏱ {{ fmtDuration(lead.response_minutes) }}
                    </span>
                  </span>
                </a>
              </div>
            </template>

            <!-- Detalhe de leads (agrupado por vendedor) -->
            <template v-else-if="detailKind === 'leads'">
              <div
                v-if="!detailLeadGroups.length"
                class="py-8 text-sm text-center text-n-slate-10"
              >
                Nenhum lead nesta categoria.
              </div>
              <div
                v-for="group in detailLeadGroups"
                :key="group.seller.id"
                class="flex flex-col gap-1"
              >
                <span
                  class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
                >
                  {{ group.seller.available_name || group.seller.name }} ({{
                    group.leads.length
                  }})
                </span>
                <a
                  v-for="lead in group.leads"
                  :key="lead._kind + '-' + lead.display_id"
                  :href="convUrl(lead.display_id)"
                  class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
                >
                  <span class="truncate text-n-slate-12">
                    {{ lead.contact_name }}
                    <span class="text-n-slate-9">#{{ lead.display_id }}</span>
                  </span>
                  <span class="flex-shrink-0 text-[11px]">
                    <span v-if="lead._kind === 'lost'" class="text-amber-700">
                      não atendeu → p/
                      <span class="font-semibold">{{
                        lead.reassigned_to
                      }}</span>
                    </span>
                    <span
                      v-else-if="lead._kind === 'pending'"
                      class="font-semibold tabular-nums"
                      :style="{
                        color:
                          commercial && lead.circulating
                            ? urgencyColor(remainingFor(lead))
                            : '#94a3b8',
                      }"
                    >
                      <template v-if="commercial && lead.circulating">
                        {{ fmtCountdown(remainingFor(lead)) }}
                      </template>
                      <template v-else>{{
                        commercial ? '>24h' : 'Aurora'
                      }}</template>
                    </span>
                    <span v-else class="inline-flex items-center gap-2">
                      <span class="text-n-slate-10">
                        {{ fmtHora(lead.first_inbound_at) }} →
                        {{ fmtHora(lead.first_reply_at) }}
                      </span>
                      <span
                        v-if="lead.answered"
                        class="font-semibold"
                        :style="{ color: slaColor(lead.response_minutes) }"
                      >
                        ⏱ {{ fmtDuration(lead.response_minutes) }}
                      </span>
                      <span v-else class="font-semibold text-amber-600"
                        >⏳</span
                      >
                    </span>
                  </span>
                </a>
              </div>
            </template>

            <!-- Detalhe de vendedores -->
            <template v-else>
              <div
                v-if="detailModal.type === 'offline'"
                class="flex flex-wrap items-center gap-3"
              >
                <div class="relative min-w-[240px] flex-1">
                  <input
                    v-model="detailSearch"
                    type="search"
                    placeholder="Buscar vendedor..."
                    class="reset-base no-margin block h-10 w-full appearance-none rounded-lg border border-solid border-n-weak bg-white pl-9 pr-3 text-sm text-n-slate-12 outline-none ring-0 placeholder:text-n-slate-10 focus:border-violet-300 focus:ring-0 !mb-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none"
                  />
                  <span
                    class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
                  />
                </div>
                <div
                  class="inline-flex h-10 flex-shrink-0 items-center gap-2 rounded-lg border border-n-weak bg-n-slate-2 px-3 text-sm font-semibold text-n-slate-12"
                >
                  <span class="i-lucide-wifi-off size-4 text-n-slate-10" />
                  <span>{{ detailSellers.length }} offline</span>
                </div>
              </div>

              <div
                v-if="!detailFilteredSellers.length"
                class="py-8 text-sm text-center text-n-slate-10"
              >
                Nenhum vendedor nesta categoria.
              </div>
              <div
                v-for="s in detailFilteredSellers"
                :key="s.id"
                class="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-n-alpha-1"
              >
                <span
                  class="relative inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-n-slate-3 text-sm font-semibold text-n-slate-11"
                >
                  {{ leadInitials(s.available_name || s.name) }}
                  <span
                    class="absolute bottom-0 right-0 size-3 rounded-full border-2 border-white"
                    :style="{ backgroundColor: statusColor(s.status) }"
                  />
                </span>
                <div class="flex flex-col min-w-0 grow">
                  <span
                    class="text-base font-medium truncate text-n-slate-12"
                    >{{ s.available_name || s.name }}</span
                  >
                  <span class="text-sm truncate text-n-slate-10">{{
                    s.email
                  }}</span>
                </div>
                <span
                  class="flex flex-shrink-0 items-center gap-3 text-xs text-n-slate-10"
                >
                  <span class="inline-flex items-center gap-1">
                    <span class="i-lucide-inbox size-3.5" />
                    {{ s.received_today }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <span class="i-lucide-message-circle-more size-3.5" />
                    {{ s.answered_today }}
                  </span>
                  <span
                    v-if="!isRodizioEffective(s)"
                    class="inline-flex items-center gap-1 text-n-ruby-11"
                  >
                    <span class="i-lucide-pause-circle size-3.5" />
                    {{ s.availability_label || 'fora' }}
                  </span>
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Modal de desativacao / redistribuicao -->
    <teleport to="body">
      <div
        v-if="modalOpen"
        class="fixed inset-0 z-[10050] flex items-center justify-center p-4"
        style="background: rgba(0, 0, 0, 0.45)"
        @click.self="closeModal"
      >
        <div
          class="w-full max-w-md p-5 border shadow-xl rounded-2xl bg-n-surface-1 border-n-weak"
        >
          <h2 class="text-base font-semibold text-n-slate-12">
            Tirar
            {{ modalAgent && (modalAgent.available_name || modalAgent.name) }}
            do rodízio
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            O que fazer com as conversas abertas e pendentes dele?
          </p>

          <div class="flex flex-col gap-2 mt-4">
            <label
              class="flex items-center gap-2 p-3 text-sm border rounded-lg cursor-pointer border-n-weak text-n-slate-12"
              :class="redistribMode === 'equal' ? 'bg-n-alpha-2' : ''"
            >
              <input type="radio" value="equal" v-model="redistribMode" />
              Distribuir igualmente entre os vendedores ativos
            </label>

            <label
              class="flex flex-col gap-2 p-3 text-sm border rounded-lg cursor-pointer border-n-weak text-n-slate-12"
              :class="redistribMode === 'specific' ? 'bg-n-alpha-2' : ''"
            >
              <span class="flex items-center gap-2">
                <input type="radio" value="specific" v-model="redistribMode" />
                Enviar para um vendedor específico
              </span>
              <select
                v-if="redistribMode === 'specific'"
                v-model="targetId"
                class="px-2 py-1.5 mt-1 text-sm border rounded-md border-n-weak bg-n-surface-2 text-n-slate-12"
              >
                <option :value="null" disabled>Selecione um vendedor…</option>
                <option
                  v-for="s in activeSellersForModal"
                  :key="s.id"
                  :value="s.id"
                >
                  {{ s.available_name || s.name }}
                </option>
              </select>
            </label>

            <label
              class="flex items-center gap-2 p-3 text-sm border rounded-lg cursor-pointer border-n-weak text-n-slate-12"
              :class="redistribMode === 'none' ? 'bg-n-alpha-2' : ''"
            >
              <input type="radio" value="none" v-model="redistribMode" />
              Não redistribuir (manter as conversas com ele)
            </label>
          </div>

          <div class="flex justify-end gap-2 mt-5">
            <button
              type="button"
              class="px-3 py-1.5 text-sm font-medium rounded-lg text-n-slate-12 bg-n-surface-2 hover:bg-n-alpha-2"
              :disabled="modalBusy"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm font-medium text-white rounded-lg"
              style="background-color: #ef4444"
              :disabled="
                modalBusy || (redistribMode === 'specific' && !targetId)
              "
              @click="confirmDeactivate"
            >
              {{ modalBusy ? 'Aplicando…' : 'Tirar do rodízio' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Toast -->
    <div
      v-if="toast"
      class="fixed z-[10000] px-4 py-2 text-sm text-white -translate-x-1/2 rounded-lg shadow-lg bottom-6 left-1/2"
      style="background-color: #0f172a"
    >
      {{ toast }}
    </div>
  </div>
</template>
